import {
  IProjectInput,
  IWorkingScheduleDay,
  IWorkingScheduleDayInput,
  Maybe,
} from '~/client/graph'

/**
 * Day numbers match `Project.workingDays` and the Sunday-first
 * `ProjectDateStore.weekdayNamesList` (index + 1): 1 = Sunday … 7 = Saturday.
 */
const WEEKDAY_NUMBERS: readonly number[] = [1, 2, 3, 4, 5, 6, 7]

/**
 * The weekday numbers a schedule works, ascending.
 *
 * `workingSchedule` is the source of truth for which days are worked: a weekday
 * listed there is worked, and the server sends the *effective* schedule, so a
 * project that never stored one still arrives with its legacy days expanded into
 * rows. Nothing here needs `workingDays`, which is on its way out (STX1-983).
 */
export function getScheduleWorkingDays(
  workingSchedule: readonly Maybe<IWorkingScheduleDay>[] | undefined,
): number[] {
  // The schema makes the rows themselves nullable, so read through rather than
  // destructure — this sits under `projectWorkingDaysMap`, which every calendar
  // render asks, and a null row there would throw rather than mislead.
  const days = (workingSchedule || [])
    .map(day => day?.dayOfWeek)
    .filter((dayOfWeek): dayOfWeek is number => dayOfWeek != null)

  return Array.from(new Set(days)).sort((a, b) => a - b)
}

/**
 * The one window that best stands for the whole week — the range the most days
 * share, ties going to the range worked on the earliest weekday.
 *
 * The tie-break is on `dayOfWeek`, not on position, so the answer is a function
 * of the schedule and cannot drift when rows arrive in a different order.
 *
 * This is what the legacy `workingHours` held, and the server derives it the
 * same way. It is the right answer only where a single week-shaped number is
 * wanted (a shared calendar axis, a default for a day with no hours of its own);
 * anything asking about a *date* wants `getWorkingHoursForDate`.
 */
export function getRepresentativeWorkingHours(
  workingSchedule: readonly Maybe<IWorkingScheduleDay>[] | undefined,
  getTimeOfDay: (time: number) => number,
): IWorkingHoursLike | null {
  const dayed = (workingSchedule || []).filter(
    day => day?.startTime != null && day?.endTime != null,
  )
  if (!dayed.length) {
    return null
  }

  const counts = new Map<string, { count: number; day: IWorkingScheduleDay }>()

  dayed.forEach(day => {
    const key = `${getTimeOfDay(day.startTime)}-${getTimeOfDay(day.endTime)}`
    const seen = counts.get(key)

    if (!seen) {
      counts.set(key, { count: 1, day })
      return
    }

    seen.count++
    // The earliest day a range is worked, so the tie-break below reads the
    // schedule's content rather than the order it happened to arrive in.
    if (day.dayOfWeek < seen.day.dayOfWeek) {
      seen.day = day
    }
  })

  const [best] = Array.from(counts.values()).sort(
    (a, b) => b.count - a.count || a.day.dayOfWeek - b.day.dayOfWeek,
  )

  return { startTime: best.day.startTime, endTime: best.day.endTime }
}

/** One row of the project's week — whether it is worked, and that day's window. */
export interface IProjectDaySchedule {
  dayNumber: number
  isWorking: boolean
  /** Time-of-day carriers in ms, same encoding as `Project.workingHours`. */
  startTime: number
  endTime: number
}

export enum ProjectScheduleMode {
  Everyday = 'everyday',
  Custom = 'custom',
}

interface IWorkingHoursLike {
  startTime: Date | number
  endTime: Date | number
}

/** Project schedule fields as the save mutation takes them. */
type IProjectScheduleInput = Pick<IProjectInput, 'workingSchedule'>

/**
 * Expands the project into a row per weekday.
 *
 * `workingSchedule` is the source of truth where it exists: a weekday listed
 * there is worked and carries its own window. `workingDays` + the project's
 * single `workingHours` are the fallback — a project saved before per-day hours
 * existed, or one whose row is missing a time.
 *
 * `workingHours` is passed in already resolved (see
 * `ProjectDateStore.projectWorkingHours`, which falls back to the
 * `DEFAULT_WORK_*_HOUR` defaults), so this stays free of date/timezone logic.
 */
export function buildProjectSchedule(
  workingDays: readonly number[],
  workingHours: IWorkingHoursLike,
  workingSchedule?: readonly Maybe<IWorkingScheduleDay>[],
): IProjectDaySchedule[] {
  const startTime = new Date(workingHours.startTime).getTime()
  const endTime = new Date(workingHours.endTime).getTime()
  const hasSchedule = !!workingSchedule?.length

  return WEEKDAY_NUMBERS.map(dayNumber => {
    const day = workingSchedule?.find(row => row?.dayOfWeek === dayNumber)
    const hasOwnWindow = day?.startTime != null && day?.endTime != null

    return {
      dayNumber,
      isWorking: hasSchedule ? !!day : workingDays.includes(dayNumber),
      startTime: hasOwnWindow ? day.startTime : startTime,
      endTime: hasOwnWindow ? day.endTime : endTime,
    }
  })
}

/**
 * The mode a stored schedule reads as: the whole week worked on one window is
 * `Everyday`, anything else is `Custom`.
 *
 * Nothing stores the mode — `workingSchedule` is seven day rows, and Everyday
 * is just the shape uniform data takes — so it is inferred once on load and is
 * the user's explicit choice from then on. A Custom week that happens to be
 * uniform therefore reads back as Everyday.
 *
 * `getTimeOfDay` maps a timestamp to its time-of-day in the project's timezone
 * (`ProjectDateStore.getDaySeconds`). Comparing raw timestamps would not do:
 * per-day windows are stored as instants, so two days both meaning 08:00 differ
 * by whole days if they were saved on different dates.
 */
export function getProjectScheduleMode(
  schedule: readonly IProjectDaySchedule[],
  getTimeOfDay: (time: number) => number,
): ProjectScheduleMode {
  const [firstDay] = schedule
  if (!firstDay) {
    return ProjectScheduleMode.Custom
  }

  const firstStart = getTimeOfDay(firstDay.startTime)
  const firstEnd = getTimeOfDay(firstDay.endTime)

  const isEveryday = schedule.every(
    day =>
      day.isWorking &&
      getTimeOfDay(day.startTime) === firstStart &&
      getTimeOfDay(day.endTime) === firstEnd,
  )

  return isEveryday ? ProjectScheduleMode.Everyday : ProjectScheduleMode.Custom
}

/**
 * Collapses the schedule back to the mutation's fields — a row per worked day.
 *
 * Only `workingSchedule` is sent: where it is provided it wins, and the server
 * re-derives `workingDays` and `workingHours` from it. Writing our own values
 * for those would be discarded, and any disagreement would be invisible.
 *
 * Half-filled ranges are the one shape the server rejects, and this can't
 * produce them — the editor always yields both times. A zero-length window is a
 * 24-hour day and an inverted one runs overnight; both are legal.
 */
export function toProjectScheduleInput(
  schedule: readonly IProjectDaySchedule[],
): IProjectScheduleInput {
  return {
    workingSchedule: schedule
      .filter(day => day.isWorking)
      .map(toWorkingScheduleDay),
  }
}

function toWorkingScheduleDay(
  day: IProjectDaySchedule,
): IWorkingScheduleDayInput {
  return {
    dayOfWeek: day.dayNumber,
    startTime: day.startTime,
    endTime: day.endTime,
  }
}

/**
 * Whether a window's end lands on the day after its start — it has run through
 * midnight. An end equal to the start is the 24-hour window, which also ends the
 * next day.
 *
 * Both arguments must share a unit (minutes or seconds of the day). This is the
 * display rule; `getWorkingMinuteSpansForDate` answers the related but distinct
 * question of which calendar days a window puts worked hours on, where a
 * 24-hour window covers its own day and spills nowhere.
 */
export function doesWindowEndNextDay(start: number, end: number): boolean {
  return end <= start
}

export function withToggledWorkingDay(
  schedule: readonly IProjectDaySchedule[],
  dayNumber: number,
): IProjectDaySchedule[] {
  return schedule.map(day =>
    day.dayNumber === dayNumber ? { ...day, isWorking: !day.isWorking } : day,
  )
}

export function withDayHours(
  schedule: readonly IProjectDaySchedule[],
  dayNumber: number,
  startTime: number,
  endTime: number,
): IProjectDaySchedule[] {
  return schedule.map(day =>
    day.dayNumber === dayNumber ? { ...day, startTime, endTime } : day,
  )
}

/** Applies one window to the whole week — the Everyday row's editor. */
export function withEverydayHours(
  schedule: readonly IProjectDaySchedule[],
  startTime: number,
  endTime: number,
): IProjectDaySchedule[] {
  return schedule.map(day => ({ ...day, startTime, endTime }))
}

/**
 * Works the whole week on one window — the first worked day's, so the range the
 * user was already looking at survives the switch to Everyday. Going the other
 * way needs nothing: Custom keeps the days as they are and the user narrows
 * them down.
 */
export function withEverydayWeek(
  schedule: readonly IProjectDaySchedule[],
): IProjectDaySchedule[] {
  const workingDays = schedule.filter(day => day.isWorking)
  const [primaryDay] = workingDays.length ? workingDays : schedule
  if (!primaryDay) {
    return schedule.slice()
  }

  return schedule.map(day => ({
    ...day,
    isWorking: true,
    startTime: primaryDay.startTime,
    endTime: primaryDay.endTime,
  }))
}
